import * as THREE from 'three';

export interface SolarFlareConfig {
  frequency: number;      // Flares per second
  intensity: number;      // Base intensity multiplier
  duration: number;       // Duration in seconds
  maxHeight: number;      // Maximum flare height from sun surface
  width: number;          // Base width of flare
  segments: number;       // Number of segments for flare geometry
}

export interface FlareEvent {
  id: string;
  startTime: number;
  duration: number;
  intensity: number;
  direction: THREE.Vector3;
  height: number;
  width: number;
  progress: number;       // 0-1
  state: 'growing' | 'peak' | 'fading';
}

export class SolarFlares extends THREE.Group {
  private config: SolarFlareConfig;
  private sunRadius: number;
  
  // Active flares
  private activeFlares: Map<string, FlareEvent> = new Map();
  private flareIdCounter: number = 0;
  
  // Visual components
  private flareMeshes: Map<string, THREE.Mesh> = new Map();
  private flareMaterial!: THREE.ShaderMaterial;
  
  // Timing
  private time: number = 0;
  private lastFlareTime: number = 0;
  private activityLevel: number = 0;
  
  // Activity tracking
  private recentActivity: number[] = [];
  private maxActivityHistory: number = 30; // 30 samples
  
  constructor(sunRadius: number, config?: Partial<SolarFlareConfig>) {
    super();
    
    this.sunRadius = sunRadius;
    this.config = {
      frequency: 0.1,      // 1 flare per 10 seconds by default
      intensity: 1.0,
      duration: 3.0,       // 3 second flares
      maxHeight: 5.0,      // 5 units from surface
      width: 2.0,
      segments: 16,
      ...config,
    };
    
    this.createFlareMaterial();
    this.userData = { id: 'solar-flares', type: 'effects' };
  }
  
  private createFlareMaterial(): void {
    this.flareMaterial = new THREE.ShaderMaterial({
      uniforms: {
        time: { value: 0.0 },
        intensity: { value: 1.0 },
        progress: { value: 0.0 },
        baseColor: { value: new THREE.Color(0xff6600) }, // Orange-red
        tipColor: { value: new THREE.Color(0xffaa00) },  // Yellow
        glowColor: { value: new THREE.Color(0xffffff) }, // White glow
        opacity: { value: 1.0 },
      },
      vertexShader: this.getFlareVertexShader(),
      fragmentShader: this.getFlareFragmentShader(),
      transparent: true,
      blending: THREE.AdditiveBlending,
      side: THREE.DoubleSide,
    });
  }
  
  private getFlareVertexShader(): string {
    return `
      uniform float time;
      uniform float progress;
      uniform float intensity;
      
      varying vec2 vUv;
      varying float vHeight;
      varying vec3 vPosition;
      
      // Simple noise function
      float noise(vec3 p) {
        return fract(sin(dot(p, vec3(12.9898, 78.233, 45.164))) * 43758.5453);
      }
      
      void main() {
        vUv = uv;
        vHeight = uv.y;
        
        vec3 pos = position;
        
        // Add turbulence to flare shape
        float turbulence = noise(pos * 5.0 + time * 2.0) * 0.1;
        float heightFactor = uv.y;
        
        // Flare widens and narrows based on height and progress
        float widthMod = sin(heightFactor * 3.14159) * progress;
        pos.x *= widthMod * (1.0 + turbulence);
        pos.z *= widthMod * (1.0 + turbulence);
        
        // Add some random movement
        pos.x += sin(time * 3.0 + pos.y * 2.0) * 0.1 * intensity;
        pos.z += cos(time * 2.5 + pos.y * 1.5) * 0.1 * intensity;
        
        vPosition = pos;
        
        gl_Position = projectionMatrix * modelViewMatrix * vec4(pos, 1.0);
      }
    `;
  }
  
  private getFlareFragmentShader(): string {
    return `
      uniform vec3 baseColor;
      uniform vec3 tipColor;
      uniform vec3 glowColor;
      uniform float progress;
      uniform float intensity;
      uniform float opacity;
      
      varying vec2 vUv;
      varying float vHeight;
      varying vec3 vPosition;
      
      void main() {
        // Color gradient from base to tip
        vec3 color = mix(baseColor, tipColor, vHeight);
        
        // Add glow at the center
        float centerDist = abs(vUv.x - 0.5) * 2.0;
        float glow = 1.0 - centerDist;
        glow = pow(glow, 2.0);
        
        color = mix(color, glowColor, glow * 0.3);
        
        // Intensity based on height and progress
        float heightFactor = sin(vHeight * 3.14159);
        float finalIntensity = intensity * progress * heightFactor;
        
        color *= finalIntensity;
        
        // Alpha falloff at edges
        float alpha = glow * progress * opacity;
        alpha *= (1.0 - vHeight * 0.3); // Fade out at tip
        
        gl_FragColor = vec4(color, alpha);
      }
    `;
  }
  
  public updateActivity(transactionRate: number): void {
    // Track recent activity for flare frequency modulation
    this.recentActivity.push(transactionRate);
    if (this.recentActivity.length > this.maxActivityHistory) {
      this.recentActivity.shift();
    }
    
    // Calculate average activity level
    const avgActivity = this.recentActivity.reduce((sum, rate) => sum + rate, 0) / this.recentActivity.length;
    this.activityLevel = Math.min(avgActivity / 100.0, 2.0); // Normalize to 0-2 range
  }

  public updateFromNetworkStats(stats: { hashrate: number; transactionRate: number; difficulty: number }): void {
    // Convert hashrate to activity level (normalized)
    const hashrateActivity = Math.min(stats.hashrate / 1e20, 1.0);
    
    // Convert transaction rate to frequency
    const transactionFrequency = Math.min(stats.transactionRate / 10, 2.0);
    
    // Update flare system
    this.updateActivity(stats.transactionRate);
    this.setIntensity(hashrateActivity);
    this.setFrequency(transactionFrequency);
    
    // Trigger special flares for high activity
    if (stats.transactionRate > 20) {
      this.triggerActivityFlare(hashrateActivity);
    }
  }
  
  public triggerFlare(direction?: THREE.Vector3, intensity?: number): string {
    const flareId = `flare-${this.flareIdCounter++}`;
    
    // Use provided direction or generate random one
    const flareDirection = direction?.clone() || this.generateRandomDirection();
    
    // Calculate flare properties
    const flareIntensity = intensity || (0.5 + Math.random() * 0.5) * this.config.intensity;
    const flareHeight = this.config.maxHeight * (0.7 + Math.random() * 0.3);
    const flareWidth = this.config.width * (0.8 + Math.random() * 0.4);
    const flareDuration = this.config.duration * (0.8 + Math.random() * 0.4);
    
    // Create flare event
    const flareEvent: FlareEvent = {
      id: flareId,
      startTime: this.time,
      duration: flareDuration,
      intensity: flareIntensity,
      direction: flareDirection,
      height: flareHeight,
      width: flareWidth,
      progress: 0,
      state: 'growing',
    };
    
    this.activeFlares.set(flareId, flareEvent);
    this.createFlareMesh(flareEvent);
    
    return flareId;
  }
  
  private generateRandomDirection(): THREE.Vector3 {
    // Generate random direction on sphere surface
    const theta = Math.random() * Math.PI * 2;
    const phi = Math.acos(2 * Math.random() - 1);
    
    return new THREE.Vector3(
      Math.sin(phi) * Math.cos(theta),
      Math.sin(phi) * Math.sin(theta),
      Math.cos(phi)
    );
  }
  
  private createFlareMesh(flareEvent: FlareEvent): void {
    // Create flare geometry
    const geometry = new THREE.CylinderGeometry(
      0.1,                    // Top radius (narrow)
      flareEvent.width,       // Bottom radius (wide)
      flareEvent.height,      // Height
      this.config.segments,   // Radial segments
      8                       // Height segments
    );
    
    // Clone material for this flare
    const material = this.flareMaterial.clone();
    material.uniforms['intensity'].value = flareEvent.intensity;
    
    const mesh = new THREE.Mesh(geometry, material);
    
    // Position flare on sun surface
    const surfacePosition = flareEvent.direction.clone().multiplyScalar(this.sunRadius);
    mesh.position.copy(surfacePosition);
    
    // Orient flare outward from sun
    mesh.lookAt(surfacePosition.clone().add(flareEvent.direction));
    
    // Start at base of flare
    mesh.position.add(flareEvent.direction.clone().multiplyScalar(flareEvent.height * 0.5));
    
    this.add(mesh);
    this.flareMeshes.set(flareEvent.id, mesh);
  }
  
  public update(deltaTime: number): void {
    this.time += deltaTime;
    
    // Update existing flares
    this.updateActiveFlares(deltaTime);
    
    // Check for new flare triggers
    this.checkFlareGeneration(deltaTime);
    
    // Update material time
    this.flareMaterial.uniforms['time'].value = this.time;
  }
  
  private updateActiveFlares(_deltaTime: number): void {
    const flaresToRemove: string[] = [];
    
    for (const [flareId, flareEvent] of this.activeFlares.entries()) {
      const elapsed = this.time - flareEvent.startTime;
      const progress = Math.min(elapsed / flareEvent.duration, 1.0);
      
      // Update flare state
      if (progress < 0.3) {
        flareEvent.state = 'growing';
      } else if (progress < 0.7) {
        flareEvent.state = 'peak';
      } else {
        flareEvent.state = 'fading';
      }
      
      flareEvent.progress = progress;
      
      // Update flare visual
      this.updateFlareMesh(flareEvent);
      
      // Remove completed flares
      if (progress >= 1.0) {
        flaresToRemove.push(flareId);
      }
    }
    
    // Clean up completed flares
    for (const flareId of flaresToRemove) {
      this.removeFlare(flareId);
    }
  }
  
  private updateFlareMesh(flareEvent: FlareEvent): void {
    const mesh = this.flareMeshes.get(flareEvent.id);
    if (!mesh) return;
    
    const material = mesh.material as THREE.ShaderMaterial;
    
    // Update progress uniform
    material.uniforms['progress'].value = flareEvent.progress;
    
    // Update intensity based on state
    let intensityMultiplier = 1.0;
    switch (flareEvent.state) {
      case 'growing':
        intensityMultiplier = flareEvent.progress / 0.3;
        break;
      case 'peak':
        intensityMultiplier = 1.0;
        break;
      case 'fading':
        intensityMultiplier = 1.0 - ((flareEvent.progress - 0.7) / 0.3);
        break;
    }
    
    material.uniforms['intensity'].value = flareEvent.intensity * intensityMultiplier;
    material.uniforms['opacity'].value = intensityMultiplier;
    
    // Scale effect based on progress
    const scaleProgress = Math.sin(flareEvent.progress * Math.PI);
    mesh.scale.setScalar(scaleProgress);
  }
  
  private checkFlareGeneration(deltaTime: number): void {
    // Activity-modified frequency
    const activityMultiplier = 1.0 + this.activityLevel;
    const effectiveFrequency = this.config.frequency * activityMultiplier;
    
    // Check if enough time has passed for potential flare
    const timeSinceLastFlare = this.time - this.lastFlareTime;
    const averageInterval = 1.0 / effectiveFrequency;
    
    if (timeSinceLastFlare > averageInterval * 0.5) {
      // Random chance based on activity level
      const flareChance = effectiveFrequency * deltaTime * (1 + this.activityLevel * 0.5);
      
      if (Math.random() < flareChance) {
        this.triggerFlare();
        this.lastFlareTime = this.time;
      }
    }
  }
  
  private removeFlare(flareId: string): void {
    const mesh = this.flareMeshes.get(flareId);
    if (mesh) {
      this.remove(mesh);
      mesh.geometry.dispose();
      (mesh.material as THREE.Material).dispose();
      this.flareMeshes.delete(flareId);
    }
    
    this.activeFlares.delete(flareId);
  }
  
  public triggerActivityFlare(activityBurst: number): void {
    // Trigger multiple flares for high activity events
    const flareCount = Math.min(Math.floor(activityBurst / 50) + 1, 5);
    
    for (let i = 0; i < flareCount; i++) {
      setTimeout(() => {
        const intensity = 0.8 + activityBurst / 200.0;
        this.triggerFlare(undefined, intensity);
      }, i * 200); // Stagger flares by 200ms
    }
  }
  
  public setFrequency(frequency: number): void {
    this.config.frequency = frequency;
  }
  
  public setIntensity(intensity: number): void {
    this.config.intensity = intensity;
  }
  
  public getActiveFlareCount(): number {
    return this.activeFlares.size;
  }
  
  public getFlareStats(): {
    activeFlares: number;
    averageIntensity: number;
    activityLevel: number;
    effectiveFrequency: number;
  } {
    let totalIntensity = 0;
    for (const flare of this.activeFlares.values()) {
      totalIntensity += flare.intensity;
    }
    
    const averageIntensity = this.activeFlares.size > 0 ? 
      totalIntensity / this.activeFlares.size : 0;
    
    const effectiveFrequency = this.config.frequency * (1.0 + this.activityLevel);
    
    return {
      activeFlares: this.activeFlares.size,
      averageIntensity,
      activityLevel: this.activityLevel,
      effectiveFrequency,
    };
  }
  
  public setQualityLevel(level: 'low' | 'medium' | 'high' | 'ultra'): void {
    // Adjust flare quality based on performance
    const qualitySettings = {
      low: { segments: 8, maxFlares: 3 },
      medium: { segments: 12, maxFlares: 5 },
      high: { segments: 16, maxFlares: 8 },
      ultra: { segments: 24, maxFlares: 12 },
    };
    
    const settings = qualitySettings[level];
    this.config.segments = settings.segments;
    
    // Remove excess flares if needed
    if (this.activeFlares.size > settings.maxFlares) {
      const flaresToRemove = Array.from(this.activeFlares.keys())
        .slice(settings.maxFlares);
      
      for (const flareId of flaresToRemove) {
        this.removeFlare(flareId);
      }
    }
  }
  
  public dispose(): void {
    // Remove all active flares
    for (const flareId of this.activeFlares.keys()) {
      this.removeFlare(flareId);
    }
    
    // Dispose base material
    this.flareMaterial.dispose();
    
    // Clear data
    this.activeFlares.clear();
    this.flareMeshes.clear();
    this.recentActivity = [];
  }
}